/**
 * Desktop hotel search calendar event overlays.
 *
 * Rows are served by `GET /api/recommendation/search_calendar_events` and mapped
 * in `api/mappers/Search/searchCalendarEventMap` — this module holds only the
 * shape and the value allow-lists the mapper narrows against.
 *
 * `location` is where the event physically happens, `searchPlaceIds` is where we
 * choose to surface it. They are deliberately separate — an event pinned to a
 * lat/lng still needs opt-in destination IDs to render against.
 *
 * Legend labels and hover tooltips are resolved in `useSearchCalendarEventNames`.
 * Feed `id`s carry the season year (`oktoberfest_munich_2026`), so that hook
 * matches copy on `toEventBaseSlug(id)` rather than the raw id.
 */
export const SEARCH_CALENDAR_EVENT_ICON_NAMES = [
  'line_film',
  'line_fire',
  'line_flower',
  'line_gift',
  'line_glass',
  'line_globe',
  'line_heart',
  'line_lightbulb',
  'line_moon',
  'line_music',
  'line_rainbow',
  'line_rocket',
  'line_snow_flake',
  'line_star',
  'line_sun',
  'line_ticket',
  'line_trees',
  'line_water',
  'line_wine_beer',
] as const

export type SearchCalendarEventIconName = typeof SEARCH_CALENDAR_EVENT_ICON_NAMES[number]

/** Stands in for an icon name the feed introduces that this build does not know. */
export const SEARCH_CALENDAR_EVENT_FALLBACK_ICON: SearchCalendarEventIconName = 'line_star'

/** Stands in for a missing feed colour, so the overlay never renders `undefined`. */
export const SEARCH_CALENDAR_EVENT_DEFAULT_COLOR = '#AED6F1'

/** Mirrors the API's `EventType`. The first entry of `eventTypes` is primary. */
export const SEARCH_CALENDAR_EVENT_TYPES = [
  'FESTIVAL',
  'CARNIVAL',
  'CONCERT',
  'MUSIC_FESTIVAL',
  'LIGHT_FESTIVAL',
  'FOOD_AND_DRINK',
  'MARKET',
  'HOLIDAY',
  'RELIGIOUS',
  'SEASONAL',
  'SPORTS',
  'ARTS',
  'FILM',
  'PRIDE',
  'EXPO',
  'FIREWORKS',
  'PARADE',
  'NIGHTLIFE',
  'OTHER',
  'UNKNOWN',
] as const

export type SearchCalendarEventType = typeof SEARCH_CALENDAR_EVENT_TYPES[number]

/**
 * Where the event physically happens.
 *
 * Exactly one of `placeId` or the `lat`/`lng` pair is populated — the API sends
 * coordinates only for events with no LE place of their own (Buñol, Worthy Farm).
 * This is descriptive only; rendering keys off `searchPlaceIds`.
 */
export interface SearchCalendarEventLocation {
  placeId?: string
  lat?: number
  lng?: number
}

export interface SearchCalendarEvent {
  id: string
  /** Untranslated display name, used when no localised message exists for `id`. */
  title: string
  /** Untranslated tooltip copy, used when no localised message exists for `id`. */
  shortDescription?: string
  startDate: string
  endDate: string
  /** Min 1 entry; the first is the primary type. */
  eventTypes: ReadonlyArray<SearchCalendarEventType>
  icon: SearchCalendarEventIconName
  /** Overlay fill colour (hex or CSS colour). */
  color: string
  /** 1 = highest. Curation signal only; it does not order the overlay. */
  rank?: number
  isManuallySelected: boolean
  location: SearchCalendarEventLocation
  /** Destination IDs this event is surfaced against on search. */
  searchPlaceIds: ReadonlyArray<string>
}
